import type {
  DocumentRef,
  RagChunk,
  RagCollection,
  RagQuery,
  RagService,
  UploadInput,
  UploadOptions,
} from '../../ports/rag'
import type { Page } from '../../types'
import { BackendError } from '../../errors'
import { byteLength, delay, uid } from './util'

export class MockRagService implements RagService {
  private collections: RagCollection[] = [
    {
      id: 'col_angebot',
      name: 'Pool 1',
      documentCount: 3,
      status: 'ready',
      createdAt: '2026-01-10',
    },
    { id: 'col_recht', name: 'Pool 2', documentCount: 8, status: 'ready', createdAt: '2026-01-10' },
    {
      id: 'col_katalog',
      name: 'Pool 3',
      documentCount: 124,
      status: 'indexing',
      progress: 64,
      createdAt: '2026-01-10',
    },
  ]

  private docs: DocumentRef[] = [
    {
      id: 'doc_1',
      collectionId: 'col_angebot',
      name: 'Doc1.docx',
      mimeType: 'docx',
      state: 'ready',
    },
    {
      id: 'doc_2',
      collectionId: 'col_angebot',
      name: 'Doc2.pdf',
      mimeType: 'pdf',
      state: 'ready',
      metadata: { sections: 42 },
    },
    {
      id: 'doc_3',
      collectionId: 'col_angebot',
      name: 'Doc3.xlsx',
      mimeType: 'xlsx',
      state: 'ready',
    },
  ]

  constructor(private readonly latency: number) {}

  async listCollections(): Promise<RagCollection[]> {
    await delay(this.latency)
    return [...this.collections]
  }

  async createCollection(name: string): Promise<RagCollection> {
    await delay(this.latency)
    const c: RagCollection = { id: uid('col'), name, documentCount: 0 }
    this.collections.push(c)
    return c
  }

  async deleteCollection(id: string): Promise<void> {
    await delay(this.latency)
    this.collections = this.collections.filter((c) => c.id !== id)
    this.docs = this.docs.filter((d) => d.collectionId !== id)
  }

  async upload(input: UploadInput, opts?: UploadOptions): Promise<DocumentRef> {
    const total = byteLength(input.data)
    opts?.onProgress?.(0, total)
    await delay(this.latency * 2)
    opts?.onProgress?.(total, total)
    const doc: DocumentRef = {
      id: uid('doc'),
      collectionId: input.collectionId,
      name: input.name,
      mimeType: input.mimeType,
      sizeBytes: total,
      state: 'processing',
      createdAt: new Date().toISOString(),
      metadata: input.metadata,
    }
    this.docs.push(doc)
    // Simulate async ingestion completing in the background.
    setTimeout(() => {
      doc.state = 'ready'
    }, this.latency * 20)
    return doc
  }

  async getDocument(id: string): Promise<DocumentRef> {
    await delay(this.latency)
    const doc = this.docs.find((d) => d.id === id)
    if (!doc) throw new BackendError({ code: 'not_found', message: `Document ${id} not found` })
    return doc
  }

  async listDocuments(collectionId: string): Promise<Page<DocumentRef>> {
    await delay(this.latency)
    return { items: this.docs.filter((d) => d.collectionId === collectionId) }
  }

  async deleteDocument(id: string): Promise<void> {
    await delay(this.latency)
    this.docs = this.docs.filter((d) => d.id !== id)
  }

  async waitUntilReady(
    id: string,
    opts?: { signal?: AbortSignal; intervalMs?: number },
  ): Promise<DocumentRef> {
    const interval = opts?.intervalMs ?? this.latency * 5
    for (;;) {
      if (opts?.signal?.aborted) throw new BackendError({ code: 'canceled' })
      const doc = await this.getDocument(id)
      if (doc.state === 'ready' || doc.state === 'failed') return doc
      await delay(interval)
    }
  }

  async search(query: RagQuery): Promise<RagChunk[]> {
    await delay(this.latency * 2)
    return [
      {
        documentId: 'doc_2',
        chunkId: 'c1',
        text: 'Lieferfrist 14 Tage …',
        score: 0.86,
        metadata: { section: '4.2' },
      },
      {
        documentId: 'doc_1',
        chunkId: 'c2',
        text: 'Zahlungsziel 30 Tage …',
        score: 0.71,
        metadata: { section: '3' },
      },
    ]
  }
}
